import { toast } from 'sonner';
import logger from './logger';

/**
 * Export data to JSON file
 */
export const exportToJson = (data, filename, language, fieldsToExport = null) => {
  const cleanData = data.map(item => {
    if (fieldsToExport) {
      const cleaned = {};
      fieldsToExport.forEach(field => {
        if (item[field] !== undefined) cleaned[field] = item[field];
      });
      return cleaned;
    }
    // Remove internal fields
    const { _id, ...rest } = item;
    return rest;
  });
  
  const exportStr = JSON.stringify(cleanData, null, 2);
  const blob = new Blob([exportStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `payhub_${filename}_${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success(language === 'fr' ? 'Données exportées!' : 'Data exported!');
};

/**
 * Import data from JSON file with duplicate check
 */
export const importFromJson = async (file, existingData, titleField, api, apiEndpoint, language, dataTransform = null) => {
  try {
    const text = await file.text();
    const itemsToImport = JSON.parse(text);
    
    // Get existing titles to check for duplicates
    const existingTitles = new Set(
      existingData.map(item => {
        const title = item[titleField];
        return (typeof title === 'string' ? title : (title?.fr || title?.en || '')).trim().toLowerCase();
      })
    );
    
    let imported = 0;
    let skipped = 0;
    
    for (const item of itemsToImport) {
      const itemTitle = item[titleField];
      const titleStr = (typeof itemTitle === 'string' ? itemTitle : (itemTitle?.fr || itemTitle?.en || '')).trim().toLowerCase();
      
      if (existingTitles.has(titleStr)) {
        skipped++;
        continue;
      }
      
      try {
        const dataToSend = dataTransform ? dataTransform(item) : item;
        await api.post(apiEndpoint, dataToSend);
        imported++;
        existingTitles.add(titleStr);
      } catch (err) {
        logger.error('Error importing item:', item[titleField], err);
      }
    }
    
    const msg = language === 'fr' 
      ? `${imported} éléments importés${skipped > 0 ? `, ${skipped} doublons ignorés` : ''}!`
      : `${imported} items imported${skipped > 0 ? `, ${skipped} duplicates skipped` : ''}!`;
    toast.success(msg);
    return { imported, skipped };
  } catch (err) {
    toast.error(language === 'fr' ? 'Erreur d\'import' : 'Import error');
    return { imported: 0, skipped: 0 };
  }
};

/**
 * Remove duplicate items from a collection
 */
export const removeDuplicates = async (data, titleField, idField, api, deleteEndpoint, language, fetchCallback) => {
  if (!window.confirm(language === 'fr' 
    ? 'Supprimer les éléments en double (garder le plus récent)?' 
    : 'Remove duplicate items (keep the most recent)?')) {
    return;
  }
  
  // Group by title
  const itemsByTitle = {};
  for (const item of data) {
    const title = item[titleField];
    const key = (typeof title === 'string' ? title : (title?.fr || title?.en || '')).trim().toLowerCase();
    if (!itemsByTitle[key]) {
      itemsByTitle[key] = [];
    }
    itemsByTitle[key].push(item);
  }
  
  // Find and delete duplicates
  let deleted = 0;
  for (const [, items] of Object.entries(itemsByTitle)) {
    if (items.length > 1) {
      items.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      for (let i = 1; i < items.length; i++) {
        try {
          await api.delete(`${deleteEndpoint}/${items[i][idField]}`);
          deleted++;
        } catch (err) {
          logger.error('Error deleting duplicate:', items[i][titleField], err);
        }
      }
    }
  }
  
  if (deleted > 0) {
    toast.success(language === 'fr' ? `${deleted} doublons supprimés!` : `${deleted} duplicates removed!`);
    fetchCallback();
  } else {
    toast.info(language === 'fr' ? 'Aucun doublon trouvé' : 'No duplicates found');
  }
};

/**
 * Print content in a new window - Using safe DOM manipulation
 */
export const printContent = (title, content, additionalInfo = '') => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Unable to open print window. Please allow popups.');
    return;
  }
  
  // Create document structure safely using DOM methods
  const doc = printWindow.document;
  
  // Create style element
  const style = doc.createElement('style');
  style.textContent = `
    body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    h1 { color: #003DA5; border-bottom: 3px solid #003DA5; padding-bottom: 10px; }
    h2 { color: #0052CC; margin-top: 30px; }
    .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
    .logo { font-size: 24px; font-weight: bold; color: #003DA5; }
    .date { color: #666; font-size: 14px; }
    .content { line-height: 1.8; }
    .info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 20px 0; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { padding: 12px; text-align: left; border-bottom: 1px solid #ddd; }
    th { background: #003DA5; color: white; }
    .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ddd; text-align: center; color: #666; font-size: 12px; }
    @media print { body { padding: 20px; } }
  `;
  doc.head.appendChild(style);
  
  // Set title
  doc.title = `${title} - PayHub Québec+`;
  
  // Create header
  const header = doc.createElement('div');
  header.className = 'header';
  
  const logo = doc.createElement('div');
  logo.className = 'logo';
  logo.textContent = '⚜️ PayHub Québec+';
  header.appendChild(logo);
  
  const date = doc.createElement('div');
  date.className = 'date';
  date.textContent = new Date().toLocaleDateString('fr-CA');
  header.appendChild(date);
  
  doc.body.appendChild(header);
  
  // Create title
  const h1 = doc.createElement('h1');
  h1.textContent = title;
  doc.body.appendChild(h1);
  
  // Create info section if provided
  if (additionalInfo) {
    const info = doc.createElement('div');
    info.className = 'info';
    info.textContent = additionalInfo;
    doc.body.appendChild(info);
  }
  
  // Create content - sanitize HTML content
  const contentDiv = doc.createElement('div');
  contentDiv.className = 'content';
  // Use textContent for plain text, or innerHTML only for trusted content
  if (content.includes('<')) {
    // If content contains HTML, parse it safely
    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(content, 'text/html');
    contentDiv.innerHTML = parsedDoc.body.innerHTML;
  } else {
    contentDiv.textContent = content;
  }
  doc.body.appendChild(contentDiv);
  
  // Create footer
  const footer = doc.createElement('div');
  footer.className = 'footer';
  footer.textContent = 'PayHub Québec+ - payhub.quebec.plus';
  doc.body.appendChild(footer);
  
  // Print after a short delay
  setTimeout(() => printWindow.print(), 250);
};

/**
 * Create sync package for production deployment
 */
export const createSyncPackage = async (api, language) => {
  toast.info(language === 'fr' ? 'Création du package de sync...' : 'Creating sync package...');
  
  try {
    const [notesRes, checklistRes, jobsRes] = await Promise.all([
      api.get('/admin/notes').catch(() => ({ data: { notes: [] } })),
      api.get('/admin/checklist').catch(() => ({ data: { items: [] } })),
      api.get('/admin/job-postings').catch(() => ({ data: { postings: [] } }))
    ]);
    
    const syncPackage = {
      created_at: new Date().toISOString(),
      version: '1.0',
      environment: 'preview',
      data: {
        notes: notesRes.data?.notes || [],
        checklist: checklistRes.data?.items || [],
        job_postings: jobsRes.data?.postings || jobsRes.data || []
      }
    };
    
    const exportStr = JSON.stringify(syncPackage, null, 2);
    const blob = new Blob([exportStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payhub_sync_package_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success(language === 'fr' 
      ? `Package créé! Notes: ${syncPackage.data.notes.length}, Checklist: ${syncPackage.data.checklist.length}, Positions: ${syncPackage.data.job_postings.length}`
      : `Package created! Notes: ${syncPackage.data.notes.length}, Checklist: ${syncPackage.data.checklist.length}, Positions: ${syncPackage.data.job_postings.length}`
    );
    
    return syncPackage;
  } catch (err) {
    logger.error('Sync package error:', err);
    toast.error(language === 'fr' ? 'Erreur lors de la création' : 'Error creating package');
    return null;
  }
};

/**
 * Import sync package from production
 */
export const importSyncPackage = async (file, api, language, existingData, fetchCallbacks) => {
  try {
    const text = await file.text();
    const syncPackage = JSON.parse(text);
    
    if (!syncPackage.data) {
      toast.error(language === 'fr' ? 'Format de package invalide' : 'Invalid package format');
      return null;
    }
    
    let results = { notes: 0, checklist: 0, jobs: 0, skipped: 0 };
    
    // Import notes
    if (syncPackage.data.notes?.length > 0) {
      const existingTitles = new Set(existingData.notes.map(n => n.title?.trim().toLowerCase()));
      for (const note of syncPackage.data.notes) {
        if (!existingTitles.has(note.title?.trim().toLowerCase())) {
          try {
            await api.post('/admin/notes', {
              title: note.title,
              content: note.content,
              category: note.category || 'conversation',
              is_pinned: note.is_pinned || false
            });
            results.notes++;
          } catch (e) { results.skipped++; }
        } else { results.skipped++; }
      }
    }
    
    // Import checklist
    if (syncPackage.data.checklist?.length > 0) {
      const existingTitles = new Set(existingData.checklist.map(c => c.title?.trim().toLowerCase()));
      for (const item of syncPackage.data.checklist) {
        if (!existingTitles.has(item.title?.trim().toLowerCase())) {
          try {
            await api.post('/admin/checklist', {
              title: item.title,
              description: item.description,
              priority: item.priority || 'P2',
              category: item.category || 'feature',
              status: item.status || 'pending'
            });
            results.checklist++;
          } catch (e) { results.skipped++; }
        } else { results.skipped++; }
      }
    }
    
    // Import job postings
    if (syncPackage.data.job_postings?.length > 0) {
      const existingTitles = new Set(existingData.jobPostings.map(j => j.title_fr?.trim().toLowerCase()));
      for (const job of syncPackage.data.job_postings) {
        if (!existingTitles.has(job.title_fr?.trim().toLowerCase())) {
          try {
            await api.post('/admin/job-postings', job);
            results.jobs++;
          } catch (e) { results.skipped++; }
        } else { results.skipped++; }
      }
    }
    
    toast.success(language === 'fr'
      ? `Importé: ${results.notes} notes, ${results.checklist} tâches, ${results.jobs} positions. ${results.skipped} doublons ignorés.`
      : `Imported: ${results.notes} notes, ${results.checklist} tasks, ${results.jobs} positions. ${results.skipped} duplicates skipped.`
    );
    
    // Refresh all data
    if (fetchCallbacks) {
      fetchCallbacks.forEach(cb => cb());
    }
    
    return results;
  } catch (err) {
    logger.error('Import error:', err);
    toast.error(language === 'fr' ? 'Erreur d\'import' : 'Import error');
    return null;
  }
};

/**
 * Translation mappings for common terms
 */
export const TRANSLATIONS = {
  'fr-en': {
    'Rejoignez-nous': 'Join Us',
    'Notre Mission': 'Our Mission',
    'Notre Vision': 'Our Vision',
    'Nos Valeurs': 'Our Values',
    'Positions Recherchées': 'Open Positions',
    'Contribuez à Notre Mission': 'Contribute to Our Mission',
    'Candidature spontanée': 'Spontaneous Application',
    'Proposer une idée': 'Propose an Idea',
    'Faire une suggestion': 'Make a Suggestion',
    'En attente': 'Pending',
    'Complété': 'Completed',
    'En cours': 'In Progress'
  },
  'en-fr': {
    'Join Us': 'Rejoignez-nous',
    'Our Mission': 'Notre Mission',
    'Our Vision': 'Notre Vision',
    'Our Values': 'Nos Valeurs',
    'Open Positions': 'Positions Recherchées',
    'Contribute to Our Mission': 'Contribuez à Notre Mission',
    'Spontaneous Application': 'Candidature spontanée',
    'Propose an Idea': 'Proposer une idée',
    'Make a Suggestion': 'Faire une suggestion',
    'Pending': 'En attente',
    'Completed': 'Complété',
    'In Progress': 'En cours'
  }
};

/**
 * Simple translation using word mapping
 */
export const translateText = (text, fromLang, toLang) => {
  const key = `${fromLang}-${toLang}`;
  let result = text;
  
  if (TRANSLATIONS[key]) {
    Object.entries(TRANSLATIONS[key]).forEach(([from, to]) => {
      result = result.replace(new RegExp(from, 'gi'), to);
    });
  }
  
  return result;
};
